import { isGuestTier } from "@salestrak/permissions";

import { useAppData } from "#app/lib/data";

// Permissions 2.0: User.permissions is a flat { key: true } map of granted
// registry keys (e.g. "manage.users", "create.workflows"). A direct key
// lookup matches how the server seeds and resolves cells.
//
// `permKey` is a single dot-notation key from the registry. Logical
// expressions (`|`, `&`) and context-args (issue/team objects) are not
// modeled here — call sites pass plain keys. If we need expression
// support, layer it on top.
export function usePermission(permKey: string): boolean {
  const { currentUser } = useAppData();
  const permissions = currentUser?.permissions;
  if (!permissions || !permKey) return false;
  return permissions[permKey] === true;
}

/** The client's reading of a role cell. `yes` is an unconditional grant;
 *  `if_created` / `if_shared` are grants the caller finishes deciding against
 *  the row in hand (its `createdBy` for `if_created`); `no` is none. */
export type PermissionVerdict = "yes" | "if_created" | "if_shared" | "no";

// The three-reading variant of usePermission, for keys that
// `supportsOnlyIfCreated` — a picker shows its Create row on `if_created` and
// its manage affordance only on the rows the viewer created. `permissions`
// carries the unconditional grants and `conditionalPermissions` the
// conditional cells, and an unconditional grant wins.
export function usePermissionVerdict(permKey: string): PermissionVerdict {
  const { currentUser } = useAppData();
  if (!currentUser || !permKey) return "no";
  if (currentUser.permissions?.[permKey] === true) return "yes";
  const conditional = currentUser.conditionalPermissions?.[permKey];
  return conditional === "if_created" || conditional === "if_shared"
    ? conditional
    : "no";
}

/**
 * Whether the viewer is share-only — a role without public access (Guest,
 * Limited Member, a custom role flagged the same), who sees only what is
 * shared with them. The member modules (Map, Workflows, Teams, Dashboards)
 * are workspace-wide surfaces, not shared work: they are not on a share-only
 * viewer's rail, their `G` chords do nothing, and their routes deny, while
 * shared workflows reach the viewer through Home. The inverse of
 * `useViewerCanBrowseMembers`, kept as its own name because the two gate
 * different surfaces (modules here, the member directory there). A viewer
 * whose row has not arrived yet reads as NOT share-only — the most
 * restricted reading must never be the accident of a pending bootstrap; the
 * route guards wait for it themselves.
 */
export function useViewerIsShareOnly(): boolean {
  const { currentUser } = useAppData();
  return currentUser?.hasPublicAccess === false;
}

/**
 * Whether member profiles are available to the viewer at all. A Guest (every
 * held role at the Guest rung — see `isGuestTier`) is an outsider, a client
 * reviewing shared work, and sees no member's profile, hover card, "Profile"
 * row action, members directory or org chart. A viewer whose roles have not
 * arrived yet reads as able; the route guards wait for the bootstrap
 * themselves before deciding.
 */
export function useViewerCanOpenProfiles(): boolean {
  const { currentUser } = useAppData();
  return !isGuestTier(currentUser?.roles);
}

/**
 * Whether the viewer may browse the member surfaces — the members directory
 * and the org chart, with their nav rows and doors. False for the share-only
 * tiers: a role without public access (Guest, Limited Member, a custom role
 * flagged the same) sees only what is shared with them, and the roster is
 * not shared work. Distinct from `useViewerCanOpenProfiles`: a Limited
 * Member is internal and still opens colleagues' profiles from the work they
 * share; they just get no directory to browse. A viewer whose row has not
 * arrived yet reads as able; the route guard waits for the bootstrap itself.
 */
export function useViewerCanBrowseMembers(): boolean {
  const { currentUser } = useAppData();
  return currentUser?.hasPublicAccess !== false;
}

// Imperative variant. Useful in event handlers / callbacks where you
// already have the perms map in scope.
export function checkPermission(
  permissions: Record<string, unknown> | null | undefined,
  permKey: string,
): boolean {
  if (!permissions || !permKey) return false;
  return permissions[permKey] === true;
}
